import React from 'react';
import { CacheApiContext, CacheApiContextType, ServicesContext } from '~/services';
import {
    FlatGroupOptionType,
    flatGroupMakeCompoundKey,
} from '~/components/GroupSelect/FlatGroupOptionType';
import { ReferralSelectOption } from '.';

interface SpecialtyType {
    id: number;
    display_name: string;
}

interface SpecialtyGroupType {
    id: number;
    name: string;
    nuccs: SpecialtyType[];
}

interface SpecialtyResponse {
    specialization_categories?: SpecialtyGroupType[];
    items?: SpecialtyType[];
}

export function useSpecialtiesList(): FlatGroupOptionType[] {
    const services = React.useContext(ServicesContext);
    const { getCachedValue } = React.useContext(CacheApiContext) as CacheApiContextType;
    const [specialtyList, setSpecialtyList] = React.useState([]);

    React.useEffect(() => {
        (async () => {
            function specialtyMaps(data: SpecialtyResponse) {
                return data.specialization_categories
                    ?.sort((a: SpecialtyGroupType, b: SpecialtyGroupType) =>
                        a.name.localeCompare(b.name)
                    )
                    .map((x: SpecialtyGroupType) => ({
                        value: x.id,
                        label: x.name,
                        nuccs: x.nuccs.sort((a: SpecialtyType, b: SpecialtyType) =>
                            a.display_name.localeCompare(b.display_name)
                        ),
                    }));
            }
            let specialtyResult = await getCachedValue('specialty_groupings', async () => {
                const result = await services.api.call({
                    method: 'get',
                    url: `/core-api/specialization-categories`,
                });
                return specialtyMaps(result.data);
            });

            specialtyResult = specialtyResult.flatMap((x: ReferralSelectOption) => [
                {
                    groupKey: null,
                    label: x.label,
                    value: x.value.toString(),
                    optionKeys: x.nuccs!.map((u) => flatGroupMakeCompoundKey(x.value, u.id)),
                },
                ...x.nuccs!.map((y) => ({
                    groupKey: x.value.toString(),
                    label: y.display_name,
                    value: flatGroupMakeCompoundKey(x.value, y.id),
                    optionKeys: [],
                })),
            ]);

            setSpecialtyList(specialtyResult);
        })();
    }, [services, getCachedValue]);

    return specialtyList;
}
